import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/browser';
import { unauthorisedStatusCodes } from '_constants/statusCodes';
import { default as User } from '_services/Api/User';
import {
    clearCookiesAndSetLoggedOut,
    setIsLoggedIn,
} from '_services/LocalStorage/LoggedIn';
import { UserShape } from '_types/User';
import { UserStateShape } from './type';

export const getUser = createAsyncThunk('user/getUser', async () => {
    try {
        const response = await User.get();
        const user = response.data ?? null;
        if (user) {
            setUserInSentry(user);
            return response.data;
        } else {
            clearCookiesAndSetLoggedOut();
            console.error(
                'Failure on retrieving user data. Response is..:',
                response,
            );
            return undefined;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
        const statusCode = error.response?.status;
        if (unauthorisedStatusCodes.includes(statusCode)) {
            clearCookiesAndSetLoggedOut();
            removeUserInSentry();
            return undefined;
        } else {
            console.error('error when getting user is..', error);
        }
    }
});

export const logout = createAsyncThunk('user/logout', async () => {
    try {
        await User.logout();
        clearCookiesAndSetLoggedOut();
        removeUserInSentry();
        localStorage.removeItem('project_count_cache');
        window.location.href = '/auth';
        return undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
        if (error.response.status === 401) {
            clearCookiesAndSetLoggedOut();
            removeUserInSentry();
            window.location.href = '/auth';
            return undefined;
        }
    }
});

export const setUserInSentry = (user: UserShape): void => {
    Sentry.setUser({ id: user.id.toString(), email: user.email });
};

export const removeUserInSentry = (): void => {
    Sentry.getCurrentScope().setUser(null);
};

const initialState: UserStateShape = {
    data: undefined,
};

export const userSlice = createSlice({
    name: 'userSlice',
    initialState,
    reducers: {
        loginUser: (state, action: PayloadAction<UserShape>) => {
            setUserInSentry(action.payload);
            setIsLoggedIn();
            state.data = action.payload;
        },
        setUser: (state, action: PayloadAction<UserShape>) => {
            setUserInSentry(action.payload);
            state.data = action.payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getUser.pending, () => {
                globalThis.isFetchingUser = true;
            })
            .addCase(getUser.fulfilled, (state, action) => {
                state.data = action.payload;
                globalThis.isFetchingUser = false;
            })
            .addCase(getUser.rejected, () => {
                globalThis.isFetchingUser = false;
            })
            .addCase(logout.fulfilled, (state, action) => {
                state.data = action.payload;
            });
    },
});

const AuthReducer = userSlice.reducer;

export const { setUser } = userSlice.actions;

export default AuthReducer;
